import React, { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { functions } from "../../firebase-client";

const money = (minor, currency = "GBP") =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency }).format(
    Number(minor || 0) / 100,
  );

export default function SupportClubBillingPanel() {
  const [query, setQuery] = useState("");
  const [clubs, setClubs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const load = async (nextQuery = query) => {
    setBusy(true);
    setError("");
    try {
      const result = await httpsCallable(
        functions,
        "listSupportClubBilling",
      )({ query: nextQuery });
      setClubs(result.data?.clubs || []);
    } catch (err) {
      setError(err?.message || "Could not load club billing.");
    } finally {
      setBusy(false);
    }
  };
  useEffect(() => {
    load("");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const toggleLock = async (club) => {
    const locked = !club.locked;
    const reason = locked ? window.prompt("Reason shown to the club") : "";
    if (locked && !reason?.trim()) return;
    setBusy(true);
    setError("");
    try {
      await httpsCallable(
        functions,
        "manageClubBillingLock",
      )({ teamId: club.teamId, locked, reason });
      await load();
    } catch (err) {
      setError(err?.message || "Could not update the club lock.");
      setBusy(false);
    }
  };
  const retry = async (club, periodId) => {
    setBusy(true);
    setError("");
    try {
      await httpsCallable(
        functions,
        "retryClubBillingCharge",
      )({ teamId: club.teamId, periodId });
      await load();
    } catch (err) {
      setError(err?.message || "Could not retry this annual charge.");
      setBusy(false);
    }
  };
  const updateRollout = async (club, { enabled, existingCutoversEnabled }) => {
    setBusy(true);
    setError("");
    try {
      await httpsCallable(
        functions,
        "manageOptionalMembershipPaymentsRollout",
      )({
        teamId: club.teamId,
        enabled,
        newClubChoiceEnabled: enabled,
        existingCutoversEnabled: enabled && existingCutoversEnabled,
      });
      await load();
    } catch (err) {
      setError(err?.message || "Could not update the optional-payments rollout.");
      setBusy(false);
    }
  };
  return (
    <section className="admin-section">
      <div className="admin-section__header">
        <div>
          <h2>Club Billing</h2>
          <p>
            Review annual charges and manually restrict or restore unpaid clubs.
          </p>
        </div>
      </div>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          load();
        }}
        className="admin-billing-search"
      >
        <input
          className="form-input"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          type="search"
          aria-label="Search clubs by name"
          placeholder="Search by club name"
        />
        <button className="button button-primary" disabled={busy}>
          {busy ? "Loading…" : "Search"}
        </button>
      </form>
      {error && (
        <p className="modal-error" role="alert">
          {error}
        </p>
      )}
      <div className="admin-billing-list" aria-busy={busy}>
        {!busy && !error && clubs.length === 0 && (
          <p className="text-muted" role="status">{query.trim() ? "No clubs match your search. Try another club name." : "No clubs to show yet."}</p>
        )}
        {clubs.map((club) => {
          const current = club.periods?.[0];
          return (
            <article className="admin-card" key={club.teamId}>
              <div>
                <h3>{club.teamName}</h3>
                <p>
                  {club.mode.replaceAll("_", " ")} ·{" "}
                  {club.card
                    ? `${club.card.brand?.toUpperCase()} •••• ${club.card.last4}`
                    : "No billing card"}
                </p>
                {current && (
                  <p>
                    {current.qualifyingMemberCount} qualifying members ·{" "}
                    {money(current.amountMinor, current.currency)} ·{" "}
                    {current.status}
                  </p>
                )}
                {current?.attempts?.length > 0 && (
                  <p>
                    {current.attempts.length} collection attempt
                    {current.attempts.length === 1 ? "" : "s"}
                  </p>
                )}
              </div>
              <div style={{ display: "flex", gap: ".5rem", flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="button button-secondary"
                  onClick={() => updateRollout(club, { enabled: !club.rolloutEnabled, existingCutoversEnabled: false })}
                  disabled={busy}
                >
                  {club.rolloutEnabled ? "Disable pilot" : "Enable pilot"}
                </button>
                {club.rolloutEnabled && (
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => updateRollout(club, { enabled: true, existingCutoversEnabled: !club.existingCutoversEnabled })}
                    disabled={busy}
                  >
                    {club.existingCutoversEnabled ? "Pause cutovers" : "Allow cutovers"}
                  </button>
                )}
                {current && ["failed", "closed"].includes(current.status) && (
                  <button
                    type="button"
                    className="button button-secondary"
                    onClick={() => retry(club, current.id)}
                    disabled={busy}
                  >
                    Retry charge
                  </button>
                )}
                <button
                  type="button"
                  className={`button ${club.locked ? "button-primary" : "button-danger"}`}
                  onClick={() => toggleLock(club)}
                  disabled={busy}
                >
                  {club.locked ? "Unlock club" : "Lock club"}
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
